/**
 * ChannelSettingsModal — Discord-style channel settings.
 * =======================================================
 *
 * Tabs: Overview | Permissions | Typography | Danger Zone
 *
 * Reuses: TypographySettings for the Typography tab.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { X, Hash, Volume2, AlertTriangle } from 'lucide-react';
import { supabase } from '../../supabase';
import TypographySettings from '../TypographySettings';
import ChannelPermissionOverrides from '../ChannelPermissionOverrides';
import { useVaultPermissions } from '../../services/permissionsService';
import { PERMISSION_EDIT_CHANNELS, PERMISSION_DELETE_CHANNELS } from '../../core/permissions/permissions';

interface ChannelSettingsModalProps {
  channelId: string | null;
  vaultId: string;
  onClose: () => void;
  onDelete?: (channelId: string) => void;
}

type TabId = 'Overview' | 'Permissions' | 'Typography' | 'Danger Zone';

const TABS: { id: TabId; label: string }[] = [
  { id: 'Overview', label: 'Overview' },
  { id: 'Permissions', label: 'Permissions' },
  { id: 'Typography', label: 'Typography' },
  { id: 'Danger Zone', label: 'Danger Zone' },
];

export default function ChannelSettingsModal({
  channelId,
  vaultId,
  onClose,
  onDelete,
}: ChannelSettingsModalProps) {
  const { hasPerm } = useVaultPermissions(vaultId);
  const canEdit = hasPerm(PERMISSION_EDIT_CHANNELS);
  const canDelete = hasPerm(PERMISSION_DELETE_CHANNELS);

  const [activeTab, setActiveTab] = useState<TabId>('Overview');
  const [channel, setChannel] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Draft state
  const [draftName, setDraftName] = useState('');
  const [draftTopic, setDraftTopic] = useState('');
  const [draftType, setDraftType] = useState<'text' | 'voice'>('text');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Danger zone
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePending, setDeletePending] = useState(false);

  useEffect(() => {
    if (!channelId) return;
    setLoading(true);
    supabase.from('channels').select('id, name, type, customization, vault_id').eq('id', channelId).maybeSingle().then(({ data }) => {
      if (data) {
        setChannel({ ...data, topic: data.customization?.topic || '' });
        setDraftName(data.name || '');
        setDraftTopic(data.customization?.topic || '');
        setDraftType(data.type || 'text');
      }
      setLoading(false);
    });
  }, [channelId]);

  const isDirty = draftName !== (channel?.name || '') || draftTopic !== (channel?.topic || '') || draftType !== (channel?.type || 'text');

  const handleSaveOverview = async () => {
    if (!channelId) return;
    setSaving(true);
    setErrorMsg(null);
    const { error } = await supabase.from('channels').update({
      name: draftName,
      customization: { ...(channel?.customization || {}), topic: draftTopic || null },
      type: draftType,
    }).eq('id', channelId);
    if (error) setErrorMsg(error.message);
    else setChannel((prev: any) => ({ ...prev, name: draftName, topic: draftTopic, type: draftType }));
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!channelId || deleteConfirm !== channel?.name) return;
    setDeletePending(true);
    onDelete?.(channelId);
    onClose();
  };

  if (!channelId) return null;

  const isVoice = channel?.type === 'voice';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-4xl h-[85vh] bg-[#0b0e1a]/95 border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-[#0b0e1a] to-[#131830] shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${draftType === 'voice' ? 'bg-[var(--neon)]/10 text-[var(--neon)]' : 'bg-white/10 text-white/70'}`}>
              {draftType === 'voice' ? <Volume2 size={18} /> : <Hash size={18} />}
            </div>
            <div>
              <h2 className="text-white font-black tracking-widest text-sm uppercase">
                {loading ? 'Loading…' : channel?.name || 'Channel Settings'}
              </h2>
              <p className="text-[10px] text-white/40 uppercase tracking-widest mt-0.5">
                {isVoice ? 'Voice Channel' : 'Text Channel'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="px-3 py-2 rounded-xl text-white/40 hover:text-white hover:bg-white/10 transition-all text-[10px] font-black uppercase tracking-widest">
            <X size={18} />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 px-4 py-2 border-b border-white/5 bg-black/20 shrink-0 overflow-x-auto no-scrollbar">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[var(--neon)]/10 text-[var(--neon)] border border-[var(--neon)]/20'
                  : 'text-white/40 hover:text-white hover:bg-white/5 border border-transparent'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-6 no-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center h-full text-white/30 text-xs uppercase tracking-widest">Loading…</div>
          ) : (
            <>
              {/* Overview */}
              {activeTab === 'Overview' && (
                <div className="max-w-lg space-y-5">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">Channel Name</label>
                    <input
                      type="text" maxLength={48}
                      value={draftName}
                      onChange={e => setDraftName(e.target.value)}
                      disabled={!canEdit}
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[var(--neon)] disabled:opacity-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">Topic</label>
                    <input
                      type="text" maxLength={256}
                      value={draftTopic}
                      onChange={e => setDraftTopic(e.target.value)}
                      disabled={!canEdit}
                      placeholder="What's this channel about?"
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[var(--neon)] disabled:opacity-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">Channel Type</label>
                    <div className="flex gap-4">
                      <label className={`flex items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all ${draftType === 'text' ? 'bg-[var(--neon)]/10 border-[var(--neon)]/30' : 'border-white/10 bg-black/20'}`}>
                        <input type="radio" checked={draftType === 'text'} onChange={() => canEdit && setDraftType('text')} disabled={!canEdit} className="accent-[var(--neon)]" />
                        <Hash size={14} className="text-white/60" />
                        <span className="text-xs font-bold text-white">Text</span>
                      </label>
                      <label className={`flex items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all ${draftType === 'voice' ? 'bg-[var(--neon)]/10 border-[var(--neon)]/30' : 'border-white/10 bg-black/20'}`}>
                        <input type="radio" checked={draftType === 'voice'} onChange={() => canEdit && setDraftType('voice')} disabled={!canEdit} className="accent-[var(--neon)]" />
                        <Volume2 size={14} className="text-white/60" />
                        <span className="text-xs font-bold text-white">Voice</span>
                      </label>
                    </div>
                  </div>

                  {errorMsg && (
                    <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-[10px] font-bold">{errorMsg}</div>
                  )}

                  {isDirty && canEdit && (
                    <button onClick={handleSaveOverview} disabled={saving}
                      className="px-6 py-2.5 rounded-xl bg-[var(--neon)] text-black text-[10px] font-black uppercase tracking-widest hover:shadow-[0_0_15px_var(--neon)] transition-all disabled:opacity-40">
                      {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                  )}
                </div>
              )}

              {/* Permissions */}
              {activeTab === 'Permissions' && (
                <ChannelPermissionOverrides channelId={channelId} vaultId={vaultId} />
              )}

              {activeTab === 'Typography' && channel && (
                <TypographySettings
                  targetType="channel"
                  targetId={channel.id}
                  targetName={channel.name}
                  initialCustomization={channel.customization || {}}
                  onSaved={(customization) => setChannel({ ...channel, customization })}
                />
              )}

              {/* Danger Zone */}
              {activeTab === 'Danger Zone' && (
                <div className="max-w-lg space-y-4">
                  <div className="p-5 rounded-2xl border border-red-500/20 bg-red-500/5 space-y-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-black text-red-400 uppercase tracking-widest">Delete Channel</h3>
                        <p className="text-[10px] text-red-300/60 mt-1">
                          This will permanently delete <b>#{channel?.name}</b> and all its messages. This action cannot be undone.
                        </p>
                      </div>
                    </div>

                    {canDelete ? (
                      <>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">
                            Type <span className="text-red-400">{channel?.name}</span> to confirm
                          </label>
                          <input
                            type="text"
                            value={deleteConfirm}
                            onChange={e => setDeleteConfirm(e.target.value)}
                            placeholder={channel?.name}
                            className="w-full bg-black/30 border border-red-500/20 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-red-500"
                          />
                        </div>
                        <button
                          onClick={handleDelete}
                          disabled={deleteConfirm !== channel?.name || deletePending}
                          className="px-6 py-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          {deletePending ? 'Deleting…' : 'Delete Channel'}
                        </button>
                      </>
                    ) : (
                      <p className="text-[10px] text-white/30 italic">You don't have permission to delete this channel.</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
